import {
  BarsArrowDownIcon,
  BuildingOfficeIcon,
  ClipboardDocumentCheckIcon,
  HomeIcon,
  TableCellsIcon,
  UserIcon,
  UsersIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  ServerIcon,
} from "@heroicons/react/24/outline";
import SupportIcon from "components/Icon/icons/SupportIcon";
import { IconNames } from "components/Icon/Icon";
import { IoPawOutline } from "react-icons/io5";
import { TbFence } from "react-icons/tb";
import { PiTreePalmLight, PiTagLight } from "react-icons/pi";
import { FiEye, FiBox } from "react-icons/fi";
import { BsFeather } from "react-icons/bs";
import { GiCheckboxTree } from "react-icons/gi";
import { ParkConfiguration } from "types/Park";

/**
 * Helper function to conditionally include menu items
 * @param condition - The condition to check
 * @param item - The menu item to include if condition is true
 * @returns The menu item if condition is true, empty array otherwise
 */
const conditionalMenuItem = <T>(
  condition: boolean | undefined,
  item: T
): T[] => {
  return condition ? [item] : [];
};

/**
 * Example usage:
 * ...conditionalMenuItem(someCondition, {
 *   id: "some-id",
 *   label: "some-label",
 *   Icon: SomeIcon,
 *   permissionsAccessKeyForManagers: "some-permission",
 * }),
 */

export type IMenuChild = {
  id: string;
  label: string;
  Icon: any;
  disableBoard?: boolean;
  add?: boolean;
  addRightType?: string;
  addRightKey?: string;
  addRightKeyJoker?: string[];
  useWordingPlural?: boolean;
  permissionsAccessKeyForManagers?: string;
};
export type IMenuGroup = {
  label: string;
  children: IMenuChild[];
  icon: IconNames;
  useWordingPlural?: boolean;
  ignoreGroup?: boolean;
};

export type IMenuConf = Record<string, IMenuGroup>;

/** ******************************** Sections ******************************* */

const homeSection: IMenuConf = {
  home: {
    label: "",
    icon: "MdOutlineChecklist",
    ignoreGroup: true,
    children: [
      {
        id: "./",
        label: "board",
        Icon: HomeIcon,
        disableBoard: true,
      },
    ],
  },
};

const getManagementSection = (
  parkConfiguration?: ParkConfiguration | null
): IMenuConf => {
  return {
    currentPark: {
      label: "park",
      icon: "GiMineTruck",
      children: [
        {
          id: "enclosures",
          label: "enclosures",
          Icon: TbFence,
          permissionsAccessKeyForManagers: "enclosure",
        },
        {
          id: "sectors",
          label: "sectors",
          Icon: PiTreePalmLight,
          permissionsAccessKeyForManagers: "sector",
        },
        {
          id: "my-park",
          label: "myPark",
          Icon: BuildingOfficeIcon,
          permissionsAccessKeyForManagers: "myPark",
        },
      ],
    },
    animals: {
      label: "animals",
      icon: "IoPawOutline",
      children: [
        {
          id: "animals",
          label: "animals",
          Icon: IoPawOutline,
          permissionsAccessKeyForManagers: "animal",
        },
        {
          id: "species",
          label: "species",
          Icon: BsFeather,
          permissionsAccessKeyForManagers: "species",
        },
        ...conditionalMenuItem(parkConfiguration?.hasZimsImport, {
          id: "imports?type=zims",
          label: "imports-zims",
          Icon: DocumentArrowDownIcon,
          permissionsAccessKeyForManagers: "imports",
        }),
      ],
    },
    groups: {
      label: "groups",
      icon: "IoPawOutline",
      children: [
        {
          id: "observation-groups",
          label: "observation-groups",
          Icon: FiEye,
          permissionsAccessKeyForManagers: "observation-group",
        },
        {
          id: "superfamilies",
          label: "superfamilies",
          Icon: GiCheckboxTree,
          permissionsAccessKeyForManagers: "superfamily",
        },
        {
          id: "tags",
          label: "tags",
          Icon: PiTagLight,
          permissionsAccessKeyForManagers: "tag",
        },
      ],
    },
    grids: {
      label: "grids",
      icon: "TableCellsIcon",
      children: [
        {
          id: "observation-grids",
          label: "observation-grids",
          Icon: TableCellsIcon,
          permissionsAccessKeyForManagers: "observation-grid",
        },
        ...conditionalMenuItem(parkConfiguration?.hasTrainings, {
          id: "training-exercises",
          label: "training-exercises",
          Icon: FiBox,
          permissionsAccessKeyForManagers: "training-grid",
        }),
        {
          id: "imports?type=observationGrid",
          label: "imports-grids",
          Icon: DocumentArrowDownIcon,
          permissionsAccessKeyForManagers: "imports",
        },
      ],
    },
    observation: {
      label: "observations",
      icon: "FiEye",
      children: [
        {
          id: "observation-responses",
          label: "observation-responses",
          Icon: ClipboardDocumentCheckIcon,
        },
        {
          id: "balance-sheets",
          label: "balance-sheets",
          Icon: TableCellsIcon,
          permissionsAccessKeyForManagers: "balance-sheet",
        },
        {
          id: "saved-balance-sheets",
          label: "saved-balance-sheets",
          Icon: DocumentTextIcon,
          permissionsAccessKeyForManagers: "balance-sheet",
        },
      ],
    },
    accounts: {
      label: "accounts",
      icon: "UserIcon",
      children: [
        {
          id: "users",
          label: "users",
          Icon: UsersIcon,
          permissionsAccessKeyForManagers: "user",
        },
        {
          id: "my-account",
          label: "myAccount",
          Icon: UserIcon,
        },
      ],
    },
    tracking: {
      label: "tracking",
      icon: "GiMineTruck",
      children: [
        {
          id: "history",
          label: "history",
          Icon: BarsArrowDownIcon,
          permissionsAccessKeyForManagers: "history",
        },
      ],
    },
  };
};

const parksSection: IMenuChild[] = [
  {
    id: "parks",
    label: "parks",
    Icon: BuildingOfficeIcon,
  },
];

const serversSection: IMenuChild[] = [
  {
    id: "servers",
    label: "servers",
    Icon: ServerIcon,
  },
];

const supportSection: IMenuChild[] = [
  {
    id: "support",
    label: "support",
    Icon: SupportIcon,
  },
];

/** **********************************  Menus by roles ************************** */

export const superadminRootMenu: IMenuConf = {
  reglages: {
    label: "params",
    icon: "VcsSettings",
    children: [...supportSection, ...parksSection, ...serversSection],
  },
};

export const getSuperadminFilteredMenu = (
  parkConfiguration?: ParkConfiguration | null
): IMenuConf => {
  return {
    ...homeSection,
    ...getManagementSection(parkConfiguration),
    reglages: {
      label: "help",
      icon: "VcsSettings",
      children: [...supportSection],
    },
  };
};

// superadminFilteredMenu.gestion.children.push(myParkSection);
